import React, { useState, useCallback, useContext, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import Hidden from "@material-ui/core/Hidden";
import { makeStyles } from "@material-ui/core/styles";
import TicketsManagerTabs from "../../components/TicketsManagerTabs";
import Ticket from "../../components/Ticket";
import MainContainer from "../../components/MainContainer";

import { QueueSelectedProvider } from "../../context/QueuesSelected/QueuesSelectedContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { CircularProgress } from "@material-ui/core";
import { getBackendUrl } from "../../config";
import logo from "../../assets/logo.png";
import logoDark from "../../assets/logo-black.png";

const defaultTicketsManagerWidth = 520;
const minTicketsManagerWidth = 480;
const maxTicketsManagerWidth = 700;

const useStyles = makeStyles((theme) => ({
	chatContainer: {
		flex: 1,
		padding: "16px",
		height: `calc(100% - 48px)`,
		overflowY: "hidden",
		backgroundColor: "transparent",
	},
	chatPapper: {
		display: "flex",
		height: "100%",
		flex: 1,
		minHeight: 0,
	},
	contactsWrapper: {
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflowY: "hidden",
		overflowX: "hidden",
		position: "relative",
		minWidth: `${minTicketsManagerWidth}px`,
		boxSizing: "border-box",
		borderRadius: "16px",
		boxShadow: theme.palette.type === "light" 
            ? `0 0 10px ${theme.palette.primary.main}40`
            : `0 0 12px ${theme.palette.primary.main}80`,
        border: theme.palette.type === "dark" ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.primary.main}80`,
		backgroundColor: theme.palette.type === "light" ? "#FFFFFF" : "#2A2D32 !important",
	},
	messagesWrapper: {
		display: "flex",
		height: "100%",
		flexDirection: "column",
		flexGrow: 1,
		marginLeft: "16px",
		boxSizing: "border-box",
		minWidth: 0,
		borderRadius: "16px",
		overflow: "hidden",
		boxShadow: theme.palette.type === "light" 
            ? `0 0 10px ${theme.palette.primary.main}40`
            : `0 0 12px ${theme.palette.primary.main}80`,
        border: theme.palette.type === "dark" ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.primary.main}80`,
		backgroundColor: theme.palette.type === "light" ? "#FFFFFF" : "#2A2D32 !important",
	},
	welcomeMsg: {
		background: "transparent", // Inherits the inset shadow background
		display: "flex",
		justifyContent: "space-evenly",
		alignItems: "center",
		height: "100%",
		textAlign: "center",
		border: "none",
		boxShadow: "none",
		color: theme.palette.type === "light" ? "#64748B" : "#8C939D",
	},
	dragger: {
		width: "14px",
		cursor: "col-resize",
		position: "absolute",
		top: 0,
		right: "-7px",
		bottom: 0,
		zIndex: 100,
		backgroundColor: "transparent",
		userSelect: "none",
		transition: "background-color 0.2s ease",
		"&:hover": {
			backgroundColor: `${theme.palette.primary.main}33`,
			borderRadius: "7px",
		},
	},
	draggingOverlay: {
		position: "fixed",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 99999,
		cursor: "col-resize",
		userSelect: "none",
	},
	logo: {
		logo: theme.logo,
		content: "url(" + (theme.palette.type === "light"
			? theme.calculatedLogoLight()
			: theme.calculatedLogoDark()) + ")"
	},
}));

const TicketsCustom = () => {
	const { user } = useContext(AuthContext);

	// ⚠️ CORREÇÃO PRINCIPAL: Inicializar com largura padrão adequada
	const [ticketsManagerWidth, setTicketsManagerWidth] = useState(
		user?.defaultTicketsManagerWidth || defaultTicketsManagerWidth
	);

	const classes = useStyles({ ticketsManagerWidth });
	const { ticketId } = useParams();
	const ticketsManagerWidthRef = useRef(ticketsManagerWidth);

	// ⚠️ CORREÇÃO: useEffect mais robusto para inicialização
	useEffect(() => {
		// Definir largura baseada no usuário ou padrão
		const initialWidth = user?.defaultTicketsManagerWidth || defaultTicketsManagerWidth;

		// Garantir que a largura esteja dentro dos limites
		const validWidth = Math.max(
			minTicketsManagerWidth,
			Math.min(maxTicketsManagerWidth, initialWidth)
		);

		setTicketsManagerWidth(validWidth);
		ticketsManagerWidthRef.current = validWidth;
	}, [user]);

	const [isDragging, setIsDragging] = useState(false);
	const dragStartXRef = useRef(0);
	const dragStartWidthRef = useRef(0);
	const animationFrameRef = useRef(null);

	const handleSaveContact = async (value) => {
		const validValue = Math.max(minTicketsManagerWidth, Math.min(maxTicketsManagerWidth, value));
		try {
			await api.put(`/users/toggleChangeWidht/${user.id}`, {
				defaultTicketsManagerWidth: validValue
			});
		} catch (error) {
			console.error("Erro ao salvar largura:", error);
		}
	};

	const handleMouseMove = useCallback((e) => {
		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
		}
		animationFrameRef.current = requestAnimationFrame(() => {
			const delta = e.clientX - dragStartXRef.current;
			const calculatedWidth = dragStartWidthRef.current + delta;
			const validWidth = Math.max(minTicketsManagerWidth, Math.min(maxTicketsManagerWidth, calculatedWidth));
			ticketsManagerWidthRef.current = validWidth;
			setTicketsManagerWidth(validWidth);
		});
	}, []);

	const handleMouseUp = async () => {
		setIsDragging(false);
		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
		}
		document.removeEventListener("mouseup", handleMouseUp, true);
		document.removeEventListener("mousemove", handleMouseMove, true);

		const newWidth = ticketsManagerWidthRef.current;
		await handleSaveContact(newWidth);
	};

	const handleMouseDown = (e) => {
		e.preventDefault();
		setIsDragging(true);
		dragStartXRef.current = e.clientX;
		dragStartWidthRef.current = ticketsManagerWidthRef.current;
		document.addEventListener("mouseup", handleMouseUp, true);
		document.addEventListener("mousemove", handleMouseMove, true);
	};

	// ⚠️ CORREÇÃO: Garantir que a largura nunca seja 0 ou inválida
	const effectiveWidth = Math.max(minTicketsManagerWidth, ticketsManagerWidth);

	return (
		<QueueSelectedProvider>
			<MainContainer>
				<div className={classes.chatPapper}>
					<div
						className={classes.contactsWrapper}
						style={{
							width: `${effectiveWidth}px`,
							// Adicionar fallbacks importantes
							minWidth: `${minTicketsManagerWidth}px`,
							maxWidth: `${maxTicketsManagerWidth}px`,
							// Garantir visibilidade
							opacity: effectiveWidth > 0 ? 1 : 0,
							visibility: effectiveWidth > 0 ? 'visible' : 'hidden'
						}}
					>
						<TicketsManagerTabs />
						<div
							onMouseDown={handleMouseDown}
							className={classes.dragger}
						/>
						{isDragging && <div className={classes.draggingOverlay} />}
					</div>
					<div className={classes.messagesWrapper}>
						{ticketId ? (
							<Ticket />
						) : (
							<Hidden only={["sm", "xs"]}>
								<Paper square variant="outlined" className={classes.welcomeMsg}>
									<span>
										<center>
											<img className={classes.logo} width="50%" alt="" />
										</center>
										{i18n.t("chat.noTicketMessage")}
									</span>
								</Paper>
							</Hidden>
						)}
					</div>
				</div>
			</MainContainer>
		</QueueSelectedProvider>
	);
};

export default TicketsCustom;
